// AuthDeleteButton：删除账号 / 机器授权行。

import { useState } from "react";
import { Trash2 } from "lucide-react";
import { apiPostForm, APIError } from "@/api/client";
import type { AuthDeleteResponse } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { toast } from "@/components/ui/Sonner";
import { ConfirmDialog } from "./ConfirmDialog";
import {
  accountAuthDeletePath,
  machineAuthDeletePath,
} from "@/api/endpoints";
import type { AuthDomain } from "./AuthActionsCell";

export interface AuthDeleteButtonProps {
  domain: AuthDomain;
  authID: number;
  subject: string;
  actor: string;
  canWrite: boolean;
  onDeleted?: () => void;
}

function deletePath(domain: AuthDomain, authID: number) {
  return domain === "account"
    ? accountAuthDeletePath(authID)
    : machineAuthDeletePath(authID);
}

export function AuthDeleteButton({
  domain,
  authID,
  subject,
  actor,
  canWrite,
  onDeleted,
}: AuthDeleteButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const label = domain === "account" ? "账号授权" : "机器授权";

  async function fire() {
    setPending(true);
    try {
      await apiPostForm<AuthDeleteResponse>(deletePath(domain, authID), {});
      toast.success("已删除授权", { description: subject });
      onDeleted?.();
    } catch (err) {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("删除授权失败", { description: msg });
      throw err;
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="destructive"
        className="w-8 px-0"
        disabled={!canWrite || pending}
        onClick={() => setConfirmOpen(true)}
        title={`删除${label}`}
        aria-label={`删除 ${subject}`}
      >
        {pending ? "…" : <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />}
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        resource={domain === "account" ? "account_auth" : "machine_auth"}
        action="delete"
        target={`${authID}`}
        actor={actor}
        description={`删除 ${subject} 的${label}记录；如果主记录仍有关联运行态或配置，会只删除授权行。`}
        onConfirm={fire}
      />
    </>
  );
}
